import { FormArray, FormGroup } from '@angular/forms';

import { CampoContasReceberEnum } from '@shared/enums/financas/campo-contas-receber.enum';
import { CodigoSefazEnum } from '@shared/enums/financas/codigo-sefaz.enum';
import { TipoContaEnum } from '@shared/enums/financas/tipo-conta.enum';
import { TipoDestinoEnum } from '@shared/enums/financas/tipo-destino.enum';
import { ContaFinanceira } from '@shared/services/models/financas/conta-financeira.model';
import { FormaRecebimentoListaStruct } from '../../../../../shared/services/structs/financas/forma-recebimento-lista.struct';
import { IntermediadorPagamentoListaStruct } from '@shared/services/structs/financas/intermediador-pagamento-lista.struct';
import { TOLERANCIA_CENTAVO, diferencaEmCentavos, somarPercentuaisDoRateio, somarRecebimentos, somarValoresDoRateio } from './venda-pagamento.calculo';
import { perfilDaForma } from './venda-pagamento.perfil';

/** O que o `salvar()` do shell sabe e a seção não: o processo, o total e as configurações. */
export interface ContextoDaValidacaoDePagamento {
    ehOrcamento: boolean;
    /** `itens + frete + despesas − desconto` — a mesma conta da seção de Totais. */
    totalDoDocumento: number;
    /** As formas do agregado: a natureza de cada recebimento sai delas. */
    formas: FormaRecebimentoListaStruct[];
    /** Configurações ▸ Vendas ▸ Pedido ▸ "Bloquear sem forma de recebimento". Só o pedido tem. */
    isBloquearSemFormaRecebimento: boolean;
    /** Contas (com o tipo) e intermediadores do agregado: decidem se o recebimento vira título ou movimentação. */
    contas: ContaFinanceira[];
    intermediadores: IntermediadorPagamentoListaStruct[];
    /** Nomes técnicos dos campos do contas a receber marcados como obrigatórios (DF10). Só o pedido valida. */
    camposObrigatoriosContasReceber: string[];
}

/** Título e mensagem no formato do `alertService.show(titulo, mensagem, warning)`. */
export interface PendenciaDePagamento {
    titulo: string;
    mensagem: string;
}

/**
 * O que o salvamento exige da seção Pagamento (§8.0 "Salvar valida" e §8.10).
 *
 * Função PURA sobre o form, chamada pelo shell: a comunicação entre componentes é por
 * Input/Output, e o shell não chama método de filho para perguntar "está válido?". Devolve a
 * PRIMEIRA pendência encontrada, na ordem em que o usuário a resolveria de cima para baixo, ou
 * `null` quando não há o que corrigir.
 *
 * O servidor confere tudo de novo; isto é o retorno imediato, para o vendedor não montar o
 * documento inteiro e descobrir a falta só na resposta.
 */
export function validarPagamento(form: FormGroup, contexto: ContextoDaValidacaoDePagamento): PendenciaDePagamento | null {
    const recebimentos = ((form.get('pagamentos') as FormArray | null)?.controls ?? []) as FormGroup[];

    // Sem recebimento não há forma, condição nem cobertura a conferir. O PEDIDO pode exigir ao
    // menos um, por configuração; o orçamento nunca — nada dele vai para o financeiro.
    if (!recebimentos.length) {
        if (!contexto.ehOrcamento && contexto.isBloquearSemFormaRecebimento) {
            return {
                titulo: 'Sem forma de recebimento',
                mensagem: 'A empresa não permite gravar pedido de venda sem forma de recebimento. Adicione um recebimento com a forma e o valor.',
            };
        }

        return validarRateio(form, contexto.totalDoDocumento);
    }

    // §8.0: forma escolhida em TODOS os recebimentos.
    const semForma = recebimentos.filter((r) => !r.get('idFormaRecebimento')?.value).length;

    if (semForma > 0) {
        return {
            titulo: 'Recebimento sem forma',
            mensagem: `${semForma} ${semForma === 1 ? 'recebimento está' : 'recebimentos estão'} sem forma de recebimento. Escolha a forma ou remova o recebimento.`,
        };
    }

    for (const [indice, recebimento] of recebimentos.entries()) {
        const pendencia = validarRecebimento(recebimento, indice, recebimentos.length, contexto.formas);

        if (pendencia) return pendencia;
    }

    // DF10: o recebimento que vira TÍTULO precisa chegar ao Finanças com o que a configuração dele exige.
    // Só o pedido: o orçamento nunca lança financeiro. O orquestrador confere de novo no salvar.
    if (!contexto.ehOrcamento && contexto.camposObrigatoriosContasReceber.length) {
        for (const [indice, recebimento] of recebimentos.entries()) {
            const pendencia = validarCamposObrigatoriosContasReceber(form, recebimento, indice, contexto);

            if (pendencia) return pendencia;
        }
    }

    // §8.0: a soma IGUAL ao total do documento. Em centavos — dinheiro em ponto flutuante não
    // fecha em zero exato.
    const lancado = somarRecebimentos(recebimentos);
    const diferenca = diferencaEmCentavos(contexto.totalDoDocumento, lancado);

    if (diferenca > 0) {
        return {
            titulo: 'Recebimentos não cobrem o total',
            mensagem: `Falta lançar ${reais(diferenca / 100)} para cobrir o total de ${reais(contexto.totalDoDocumento)}.`,
        };
    }

    if (diferenca < 0) {
        return {
            titulo: 'Recebimentos excedem o total',
            mensagem: `Os recebimentos somam ${reais(lancado)} e excedem o total de ${reais(contexto.totalDoDocumento)} em ${reais(-diferenca / 100)}.`,
        };
    }

    return validarRateio(form, contexto.totalDoDocumento);
}

/**
 * O que a NATUREZA da forma exige de um recebimento (§8.3): condição onde há parcelamento,
 * intermediador nos cartões e no Pix/Boleto via intermediador, e a conta onde ela é escolhida
 * pelo usuário — bancária, caixa ou de recebimento obrigatória. As mesmas condições que o
 * `vd-recebimento` usa para MOSTRAR cada campo, para a tela nunca cobrar o que não exibiu.
 */
function validarRecebimento(
    recebimento: FormGroup,
    indice: number,
    total: number,
    formas: FormaRecebimentoListaStruct[],
): PendenciaDePagamento | null {
    const forma = formas.find((f) => f.id === recebimento.get('idFormaRecebimento')?.value) ?? null;
    const meioIntermediador = !!recebimento.get('idIntermediadorPagamento')?.value;
    const perfil = perfilDaForma(forma, meioIntermediador);
    const nome = `"${forma?.descricao ?? 'Novo recebimento'}" (${indice + 1}/${total})`;

    if ((Number(recebimento.get('valor')?.value) || 0) <= 0) {
        return { titulo: 'Recebimento sem valor', mensagem: `Informe o valor do recebimento ${nome}.` };
    }

    const temCondicao =
        !!recebimento.get('idCondicaoPagamento')?.value ||
        !!String(recebimento.get('condicaoPagamentoLivre')?.value ?? '').trim();

    if (perfil.condicao && !temCondicao) {
        return { titulo: 'Condição de pagamento', mensagem: `Informe a condição de pagamento do recebimento ${nome}.` };
    }

    if (perfil.intermediacao === 'cartao' && !meioIntermediador) {
        return { titulo: 'Intermediador de pagamento', mensagem: `Escolha o intermediador de pagamento do recebimento ${nome}.` };
    }

    const temConta = !!recebimento.get('idContaFinanceira')?.value;

    if ((perfil.contaBancaria || (perfil.intermediacao === 'meio' && !meioIntermediador)) && !temConta) {
        return { titulo: 'Conta bancária', mensagem: `Escolha a conta bancária do recebimento ${nome}.` };
    }

    if (perfil.contaDeRecebimento === 'contaCaixa' && !temConta) {
        return { titulo: 'Conta caixa', mensagem: `Escolha a conta caixa do recebimento ${nome}.` };
    }

    if (perfil.contaDeRecebimento === 'obrigatoria' && !temConta) {
        return { titulo: 'Conta de recebimento', mensagem: `Escolha a conta de recebimento do recebimento ${nome}.` };
    }

    return null;
}

/**
 * Regra caixa × título (projects/ATS.Hub.API.Financas/context.md), a mesma do Finanças e do salvar no orquestrador:
 * destino "Contas a receber" → título; destino de conta com conta Caixa ou Conta bancária → movimentação; conta de
 * Recebimento/Cartão → título; sem conta (crediário) → título. Nas formas que o intermediador atende (cartão, boleto
 * e Pix), com intermediador a conta é sempre a dele e o recebimento nunca vira movimentação.
 */
export function recebimentoViraMovimentacao(
    recebimento: FormGroup,
    forma: FormaRecebimentoListaStruct | null,
    contas: ContaFinanceira[],
    intermediadores: IntermediadorPagamentoListaStruct[],
): { viraMovimentacao: boolean; temConta: boolean } {
    const formaDeIntermediador = !!forma && FORMAS_DE_INTERMEDIADOR.includes(forma.codigoPagamentoSefaz as CodigoSefazEnum);
    const idIntermediador = recebimento.get('idIntermediadorPagamento')?.value as number | null;
    const intermediador = formaDeIntermediador && idIntermediador
        ? intermediadores.find((i) => i.idIntermediadorPagamento === idIntermediador) ?? null
        : null;
    const idConta: number | null = intermediador
        ? intermediador.idContaFinanceira ?? null
        : (recebimento.get('idContaFinanceira')?.value as number | null) ?? null;
    const conta = idConta ? contas.find((c) => c.idContaFinanceira === idConta) ?? null : null;
    const viraMovimentacao = !!forma && !intermediador && forma.idTipoDestino !== TipoDestinoEnum.ContasAReceber && !!conta
        && (conta.idTipoConta === TipoContaEnum.ContaCaixa || conta.idTipoConta === TipoContaEnum.ContaBancaria);

    return { viraMovimentacao, temConta: !!conta };
}

/** Formas que passam por intermediador de pagamento (códigos SEFAZ), como em `formaDeIntermediador` do Finanças. */
const FORMAS_DE_INTERMEDIADOR: CodigoSefazEnum[] = [CodigoSefazEnum.CartaoCredito, CodigoSefazEnum.CartaoDebito, CodigoSefazEnum.Boleto, CodigoSefazEnum.Pix];

/**
 * DF10: os campos obrigatórios do contas a receber que o pedido consegue atender, com as MESMAS mensagens do
 * orquestrador (`ValidarCamposObrigatoriosContasReceber`). Centro de custo: id ou rateio; vendedor; conta de
 * recebimento; NSU em todas as parcelas. Forma já é exigida pelo pedido; observação é atendida pelas
 * observações internas ou pela observação da parcela.
 */
function validarCamposObrigatoriosContasReceber(
    form: FormGroup,
    recebimento: FormGroup,
    indice: number,
    contexto: ContextoDaValidacaoDePagamento,
): PendenciaDePagamento | null {
    const forma = contexto.formas.find((f) => f.id === recebimento.get('idFormaRecebimento')?.value) ?? null;
    const { viraMovimentacao, temConta } = recebimentoViraMovimentacao(recebimento, forma, contexto.contas, contexto.intermediadores);

    if (viraMovimentacao) return null;

    const campos = contexto.camposObrigatoriosContasReceber;
    const prefixo = `Recebimento ${indice + 1}: `;
    const temRateio = form.get('isRateioCentroCusto')?.value === true
        && (((form.get('rateioCentroCusto') as FormArray | null)?.length ?? 0) > 0);
    const parcelas = ((recebimento.get('parcelas') as FormArray | null)?.controls ?? []) as FormGroup[];

    if (campos.includes(CampoContasReceberEnum.CentroCusto) && !form.get('idCentroCusto')?.value && !temRateio) {
        return { titulo: 'Centro de custo obrigatório', mensagem: prefixo + 'o contas a receber exige centro de custo; informe no pedido.' };
    }

    if (campos.includes(CampoContasReceberEnum.Vendedor) && !form.get('idVendedor')?.value) {
        return { titulo: 'Vendedor obrigatório', mensagem: prefixo + 'o contas a receber exige vendedor; informe no pedido.' };
    }

    if (campos.includes(CampoContasReceberEnum.ContaFinanceira) && !temConta) {
        return { titulo: 'Conta de recebimento obrigatória', mensagem: prefixo + 'o contas a receber exige conta de recebimento; informe a conta no recebimento.' };
    }

    if (campos.includes(CampoContasReceberEnum.Nsu)
        && (!parcelas.length || parcelas.some((p) => !String(p.get('nsu')?.value ?? '').trim()))) {
        return { titulo: 'NSU obrigatório', mensagem: prefixo + 'o contas a receber exige NSU; informe o NSU em todas as parcelas.' };
    }

    if (campos.includes(CampoContasReceberEnum.Observacoes)
        && !String(form.get('observacoesInternas')?.value ?? '').trim()
        && (!parcelas.length || parcelas.some((p) => !String(p.get('observacao')?.value ?? '').trim()))) {
        return { titulo: 'Observação obrigatória', mensagem: prefixo + 'o contas a receber exige observação; informe as observações internas do pedido ou a observação de cada parcela.' };
    }

    return null;
}

/**
 * Rateio (§8.10), com as MESMAS duas contas e a MESMA tolerância da folha (`ValidarRateio`,
 * `ToleranciaCentavo`): Σ% = 100 e Σ R$ = total do documento. A segunda existe porque o Valor (R$)
 * é editável linha a linha — os percentuais podem fechar 100 e os reais não.
 */
function validarRateio(form: FormGroup, totalDoDocumento: number): PendenciaDePagamento | null {
    if (form.get('isRateioCentroCusto')?.value !== true) return null;

    const linhas = ((form.get('rateioCentroCusto') as FormArray | null)?.controls ?? []) as FormGroup[];

    if (!linhas.length) {
        return {
            titulo: 'Rateio sem linhas',
            mensagem: 'O rateio por centro de custo está ligado, mas não tem linhas. Adicione linhas ou desligue o rateio.',
        };
    }

    if (linhas.some((l) => !l.get('idCentroCusto')?.value)) {
        return { titulo: 'Rateio incompleto', mensagem: 'Escolha o centro de custo em todas as linhas do rateio.' };
    }

    const soma = somarPercentuaisDoRateio(linhas);

    if (Math.abs(soma - 100) > TOLERANCIA_CENTAVO) {
        return {
            titulo: 'Rateio não fecha 100%',
            mensagem: `O rateio soma ${soma.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}% — ajuste para 100%.`,
        };
    }

    const somaValor = somarValoresDoRateio(linhas);

    if (Math.abs(somaValor - totalDoDocumento) > TOLERANCIA_CENTAVO) {
        return {
            titulo: 'Rateio não fecha o total',
            mensagem: `O rateio soma ${reais(somaValor)} e o total do documento é ${reais(totalDoDocumento)}.`,
        };
    }

    return null;
}

/** "R$ 1.234,56" — o mesmo `toLocaleString` que as outras telas usam nas mensagens. */
function reais(valor: number): string {
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
